import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import React, { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { Alert, Col, Menu, Popover, Row } from 'antd';

import { TAB_KEY, TaxIdentifierType } from '@portal/enum/onboardingType';
import { UserGender } from '@portal/models/enumerators/onboarding';
import { RepresentorType } from '@portal/models/enumerators';
import { getEnumDescription } from '@portal/utils/enum';
import { onboarding } from '@portal/config/app.json';
import { AuthActions, OnboardingActions } from '@portal/actions';
import { translate } from '@portal/services/i18n';
import { useDispatch } from 'react-redux';
import * as MessageService from '@portal/services/message';
import {
  isCNPJ,
  maskCpf,
  maskCpfOrCnpj,
  maskMoney,
  maskPhoneInput,
  unmaskField,
} from '@portal/services/masks';

import AdvancedDateTimePicker from '@portal/components/AdvancedDateTimePicker/AdvancedDateTimePicker';
import AdvancedAutocomplete from '@portal/components/AdvancedAutocomplete/AdvancedAutocomplete';
import OnboardingAddress from '@portal/components/OnboardingAddress/OnboardingAddress';
import AdvancedSelect from '@portal/components/AdvancedSelect/AdvancedSelect';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import AdvancedForm from '@portal/components/AdvancedForm/AdvancedForm';
import AdvancedCheckbox from '@portal/components/AdvancedCheckbox/AdvancedCheckbox';
import useReduxState from '@portal/hooks/useReduxState';
import * as StorageService from '~/services/storage';
import { Tooltip } from '@mui/material';
import { QuestionCircleOutlined } from '@ant-design/icons';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { useNavigation } from '@portal/hooks/useNavigation';

interface IProps {
  addressFields?: boolean;
  userData: models.InitialOnboarding;
  onFormChange: (key: string, value: unknown) => void;
  taxIdentifierType: TaxIdentifierType;
  representantsIndex: number;
  allPercentage: number;
  setUserDataForm: (value: models.InitialOnboarding) => void;
  setIsValidDate: (value: boolean) => void;
  setIsValidName: (value: boolean) => void;
  setIsUserMinor?: (value: boolean) => void;
  isUserMinor?: boolean;
  setIsValidMotherName: (value: boolean) => void;
  isPending?: boolean;
  tabKey?: TAB_KEY;
  setTabKey: (value: TAB_KEY) => void;
  handleRepresentsValidation?: (
    newRepresentative: models.Representants,
    isRepresentativePerson: boolean,
    validateAddress?: boolean
  ) => void;
  handleFirstStepPerson?: (newAccount: models.CreateAccount) => void;
  hidePrivacyPolicy?: boolean;
}

const CreateAccountUser = ({
  taxIdentifierType,
  setIsValidDate,
  setIsValidName,
  setIsValidMotherName,
  allPercentage,
  handleRepresentsValidation,
  tabKey,
  setTabKey,
  handleFirstStepPerson,
  hidePrivacyPolicy,
  addressFields,
  isUserMinor,
  setIsUserMinor,
}: IProps) => {
  const [name, setName] = useState('');
  const [taxIdentifier, setTaxIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [bornAt, setBornAt] = useState('');
  const [motherName, setMotherName] = useState('');
  const [cellphone, setCellphone] = useState('');
  const [identityCardIssueDate, setIdentityCardIssueDate] = useState('');
  const [identityCardNumber, setIdentityCardNumber] = useState('');
  const [businessLine, setBusinessLine] = useState<number>();
  const [financialStatistic, setFinancialStatistic] = useState<number>();
  const [type, setType] = useState(
    taxIdentifierType === TaxIdentifierType.PJ
      ? undefined
      : RepresentorType.LEGAL_REPRESENTOR
  );
  const [gender, setGender] = useState<UserGender>();
  const [companyPercentage, setCompanyPercentage] = useState<number>();
  const [address, setAddress] = useState<models.Address>();
  const [isCompany, setIsCompany] = useState(false);
  const [maskedIncome, setMaskedIncome] = useState('');
  const [income, setIncome] = useState(0);
  const [isPep, setIsPep] = useState(false);
  const [hasFiscalResidentialOutside, setHasFiscalResidentialOutside] =
    useState(false);
  const [isUsPerson, setIsUsPerson] = useState(false);

  const navigate = useNavigation<{
    accountType: TaxIdentifierType;
    tab?: TAB_KEY;
    isRetry?: boolean;
  }>();

  const dispatch = useDispatch();

  const handleLogout = () => {
    StorageService.removeItem('session-token');
    dispatch(AuthActions.logout());
    navigate('/');
  };

  const {
    onboarding: { getRepresentantAddress, settings },
  } = useReduxState();

  const handleChangedBirthday = (date: MaterialUiPickersDate) => {
    if (date) {
      const valid =
        date <= DateTime.now() &&
        date >= DateTime.fromJSDate(new Date(1899, 11, 31));

      if (valid) {
        setIsValidDate(true);
      } else {
        setIsValidDate(false);
      }

      const isMinor = DateTime.now().diff(date, 'year').toObject();

      if (setIsUserMinor && isMinor.years && !isCompany) {
        setIsUserMinor(isMinor.years < 18);
      }
    }
  };

  const onIncomeChange = (incomeValue: string) => {
    const letters = /^[a-zA-Z]*$/;

    if (incomeValue.length > 22) {
      return;
    }

    if (letters.test(incomeValue)) {
      return;
    }

    const maskedValue = maskMoney(incomeValue);
    setMaskedIncome(maskedValue);

    const formattedValue = parseFloat(
      maskedValue.substring(2).split('.').join('').replace(/,/g, '.')
    );

    setIncome(formattedValue);
  };

  const handleChangedName = (value: string) => {
    if ((value && value.split(' ')[1]) || isCompany) {
      setIsValidName(true);
    } else {
      setIsValidName(false);
    }
  };

  const handleChangedMotherName = (value: string) => {
    if (value && value.split(' ')[1]) {
      setIsValidMotherName(true);
    } else {
      setIsValidMotherName(false);
    }
  };

  const handleRepresentativeTypeOptions = () => {
    if (!settings?.representorTypes) {
      return [] as models.SelectOptions[];
    }

    const representativeOptions: models.SelectOptions[] =
      settings.representorTypes.map((type) => {
        return {
          name: type.type,
          value: type.id,
        };
      });

    if (tabKey === TAB_KEY.NEW_REPRESENTANTS && allPercentage === 100) {
      return representativeOptions.filter((option) => option.value === 2);
    }

    return representativeOptions;
  };

  const handleUserGenderOptions = () => {
    const genderOptions: models.SelectOptions[] = [];
    const genderType = UserGender;

    for (const gender in genderType) {
      if (Object.prototype.hasOwnProperty.call(genderType, gender)) {
        genderOptions.push({
          name: getEnumDescription(UserGender[gender], 'USER_GENDER'),
          value: UserGender[gender],
        });
      }
    }

    return genderOptions;
  };

  const handleAddressFormChange = (key: string, value: string) => {
    setAddress((prev) => ({
      ...(prev ?? {}),
      [key]: value,
    }));
  };

  const showIdentityCardFields = () => {
    const isPJ = taxIdentifierType === TaxIdentifierType.PJ;

    if (onboarding.identityCardPf && !isPJ && tabKey === TAB_KEY.FIRST_STEP) {
      return true;
    }

    if (
      onboarding.allowIdentifyCard &&
      isPJ &&
      [TAB_KEY.REPRESENTANTS, TAB_KEY.NEW_REPRESENTANTS].includes(
        tabKey as TAB_KEY
      )
    ) {
      return true;
    }

    return false;
  };

  const PopOverContent = (
    <div className="panel-content-top-bar__right__user-dropdown__menu">
      <Menu>
        <Menu.ItemGroup
          title={translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.IS_PEP_TEXT'
          )}
        ></Menu.ItemGroup>
      </Menu>
    </div>
  );

  const PopOverContentFacta = (
    <div className="panel-content-top-bar__right__user-dropdown__menu">
      <Menu>
        <Menu.ItemGroup
          title={translate(
            'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.FATCA.POPOVER'
          )}
        ></Menu.ItemGroup>
      </Menu>
    </div>
  );

  useEffect(() => {
    const cleanAddress = unmaskField(address?.zipCode);
    if (cleanAddress && cleanAddress.length === 8) {
      dispatch(OnboardingActions.getAddress(cleanAddress, true));
    }
  }, [address?.zipCode, dispatch]);

  useEffect(() => {
    if (getRepresentantAddress) {
      setAddress({
        ...getRepresentantAddress,
        zipCode: unmaskField(getRepresentantAddress?.zipCode),
      });
      dispatch(OnboardingActions.cleanRepresentantAddress());
    }
  }, [getRepresentantAddress, dispatch]);

  useEffect(() => {
    if (
      (tabKey === TAB_KEY.REPRESENTANTS ||
        tabKey === TAB_KEY.NEW_REPRESENTANTS) &&
      onboarding.allowCompanyRepresentative
    ) {
      setIsCompany(isCNPJ(taxIdentifier));
    }

    if (navigate?.location?.state?.isRetry) {
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxIdentifier]);

  useEffect(() => {
    if (isCompany) {
      setType(RepresentorType.CORPORATE);
    } else {
      setType(
        tabKey === TAB_KEY.MINOR_REPRESENTANTS
          ? RepresentorType.LEGAL_REPRESENTOR
          : RepresentorType.BUSINESS_PARTNER
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompany]);

  return (
    <div className="create-account-user">
      <AdvancedForm>
        <Row gutter={16}>
          {!navigate?.location?.state?.isRetry && (
            <Col xs={24} sm={12}>
              <div className="create-account-user__col">
                <AdvancedInput
                  variant="outlined"
                  label={
                    onboarding.allowCompanyRepresentative &&
                    tabKey !== TAB_KEY.FIRST_STEP
                      ? translate(
                          'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.TYPE_ACCOUNT.CPF_CNPJ'
                        )
                      : translate(
                          'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.TYPE_ACCOUNT.CPF'
                        )
                  }
                  value={
                    onboarding.allowCompanyRepresentative &&
                    tabKey !== TAB_KEY.FIRST_STEP
                      ? maskCpfOrCnpj(taxIdentifier)
                      : maskCpf(taxIdentifier)
                  }
                  onChange={(value) => {
                    if (unmaskField(value).length <= 14) {
                      setTaxIdentifier(unmaskField(value));
                    }
                  }}
                />
              </div>
            </Col>
          )}
          <Col xs={24} sm={navigate?.location?.state?.isRetry ? 24 : 12}>
            <div className="create-account-user__col">
              <AdvancedInput
                label={
                  isCompany
                    ? translate(
                        'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.COMPANY_NAME.LABEL'
                      )
                    : translate(
                        'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.USER_NAME.LABEL'
                      )
                }
                className="create-account-user__inputLabel"
                value={name}
                variant="outlined"
                onChange={(value) => {
                  handleChangedName(value);
                  setName(value);
                }}
              />
              {!isCompany && (
                <ThemedText variant="body" color="gray" className="typo-mt-xs">
                  {translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.USER_NAME.COMMENTS'
                  )}
                </ThemedText>
              )}
            </div>
          </Col>
        </Row>
        <Row gutter={16}>
          {!isCompany && (
            <Col xs={24} sm={12}>
              <div className="create-account-user__col">
                <AdvancedInput
                  label={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.MOM_NAME'
                  )}
                  variant="outlined"
                  value={motherName}
                  onChange={(value) => {
                    handleChangedMotherName(value);
                    setMotherName(value);
                  }}
                />
              </div>
            </Col>
          )}
          <Col xs={24} sm={12}>
            <div className="create-account-user__col">
              <AdvancedInput
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.EMAIL'
                )}
                value={email}
                variant="outlined"
                onChange={(value) => {
                  setEmail(value);
                }}
              />
            </div>
          </Col>
        </Row>
        <Row gutter={16} className="create-account-user__col-margin-top">
          {!navigate?.location?.state?.isRetry && (
            <Col xs={24} sm={8}>
              <div className="create-account-user__col">
                <AdvancedInput
                  variant="outlined"
                  label={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.CELLPHONE'
                  )}
                  value={maskPhoneInput(cellphone)}
                  onChange={(value) => {
                    if (unmaskField(value).length <= 11) {
                      setCellphone(unmaskField(value));
                    }
                  }}
                />
              </div>
            </Col>
          )}
          {tabKey !== TAB_KEY.MINOR_REPRESENTANTS && (
            <Col xs={24} sm={navigate?.location?.state?.isRetry ? 12 : 8}>
              <div className="create-account-user__col">
                <AdvancedDateTimePicker
                  onlyDate
                  disableFuture
                  variant="outlined"
                  label={
                    isCompany
                      ? translate(
                          'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.CORPORATIVE.OPEN'
                        )
                      : translate(
                          'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.BIRTH_DATE'
                        )
                  }
                  value={bornAt ?? null}
                  onChange={(value) => {
                    handleChangedBirthday(value);
                    if (value) {
                      setBornAt(value.toISODate());
                      setIsValidDate(true);
                    } else {
                      setIsValidDate(false);
                    }
                  }}
                />
              </div>
            </Col>
          )}
          <Col
            xs={24}
            sm={
              navigate?.location?.state?.isRetry
                ? tabKey !== TAB_KEY.MINOR_REPRESENTANTS
                  ? 12
                  : 24
                : 8
            }
          >
            <div className="create-account-user__col">
              <AdvancedInput
                variant="outlined"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.INCOME'
                )}
                value={maskedIncome}
                onChange={onIncomeChange}
              />
            </div>
          </Col>
        </Row>
        {onboarding.isPep ? (
          <Row className="create-account-user__col-margin-top">
            <Col xs={24}>
              <div className="create-account-user__full">
                <AdvancedCheckbox
                  label={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.IS_PEP'
                  )}
                  value={isPep}
                  onChange={() => setIsPep(!isPep)}
                />

                <Popover content={PopOverContent}>
                  <QuestionCircleOutlined />
                </Popover>
              </div>
            </Col>
          </Row>
        ) : null}
        {showIdentityCardFields() && (
          <Row gutter={16} className="create-account-user__col-margin-top">
            <Col xs={24} sm={12}>
              <div className="create-account-user__full">
                <AdvancedDateTimePicker
                  onlyDate
                  disableFuture
                  variant="outlined"
                  label={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.IDENTITY_CARD_DATE'
                  )}
                  value={identityCardIssueDate ?? null}
                  onChange={(value) => {
                    if (value) {
                      setIdentityCardIssueDate(value.toISODate());
                    }
                  }}
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="create-account-user__full">
                <AdvancedInput
                  variant="outlined"
                  label={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.IDENTITY_CARD_NUMBER'
                  )}
                  value={identityCardNumber}
                  onChange={(value) => setIdentityCardNumber(value)}
                />
              </div>
            </Col>
          </Row>
        )}
        {onboarding.allowGender || onboarding.moreOptions ? (
          <Row gutter={16} className="create-account-user__col-margin-top">
            <Col xs={24} sm={12}>
              <AdvancedSelect
                variant="outlined"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.GENDER'
                )}
                value={gender}
                onChange={(value) => {
                  setGender(value);
                }}
                options={handleUserGenderOptions() ?? []}
              />
            </Col>
          </Row>
        ) : null}
        {!navigate?.location?.state?.isRetry &&
        taxIdentifierType === TaxIdentifierType.PF &&
        tabKey === TAB_KEY.FIRST_STEP &&
        onboarding.moreOptions &&
        !isCompany &&
        !isUserMinor ? (
          <Row gutter={16} className="create-account-user__col-margin-top">
            <Col xs={24} sm={12}>
              <div className="create-account-user__col-margin-top create-account-user__full">
                <AdvancedAutocomplete
                  variant="outlined"
                  label={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.BUSINESS_LINES'
                  )}
                  onChange={(value) => {
                    if (typeof value === 'number') {
                      setBusinessLine(value);
                    }
                  }}
                  options={
                    settings?.businessLineMP
                      .filter((lineMP) => lineMP.type === 'PF')
                      .map((line) => {
                        return {
                          name: line.description,
                          value: line.value,
                        };
                      }) ?? []
                  }
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="create-account-user__col-margin-top create-account-user__full">
                <AdvancedAutocomplete
                  variant="outlined"
                  label={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.FINANCIAL_STATISTICS'
                  )}
                  onChange={(value) => {
                    if (typeof value === 'number') {
                      setFinancialStatistic(value);
                    }
                  }}
                  options={
                    settings?.financialStatistic.map((line) => {
                      return { name: line.description, value: line.value };
                    }) ?? []
                  }
                />
              </div>
            </Col>
          </Row>
        ) : null}
        <Row gutter={16} className="create-account-user__col-margin-top">
          <Col xs={24} sm={12}>
            {taxIdentifierType === TaxIdentifierType.PJ && (
              <AdvancedSelect
                variant="outlined"
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PJ.FIRST_STEP.FORM.REPRESENTANTS.REPRESENTANTS_TYPE'
                )}
                value={type ?? ''}
                onChange={(value) => {
                  if (value === RepresentorType.PROCURATOR) {
                    setCompanyPercentage(0);
                  }
                  setType(value);
                }}
                options={handleRepresentativeTypeOptions() ?? []}
              />
            )}
          </Col>
          <Col xs={24} sm={12}>
            {taxIdentifierType === TaxIdentifierType.PJ &&
              (type === RepresentorType.BUSINESS_PARTNER ||
                type === RepresentorType.CORPORATE) && (
                <Tooltip
                  title={translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.PORCENTAGE_TOOLTIP'
                  )}
                >
                  <AdvancedInput
                    className="num_percent"
                    variant="outlined"
                    label={translate(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.PORCENTAGE'
                    )}
                    value={companyPercentage}
                    onChange={(value) => {
                      if (value === '') {
                        setCompanyPercentage(0);
                      }

                      if (Number(value) + allPercentage > 100) {
                        return;
                      }

                      if (Number(value) <= 100) {
                        setCompanyPercentage(parseInt(value));
                      }
                    }}
                  />
                </Tooltip>
              )}
          </Col>
        </Row>
        {addressFields ? (
          <>
            {onboarding.allowHeaderAddress ? (
              <Row className="create-account-user__header-address">
                <p>
                  {translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FORM.HEADER_ADDRESS'
                  )}
                </p>
              </Row>
            ) : null}
            <OnboardingAddress
              addressData={address}
              onFormChange={handleAddressFormChange}
              small
            />
          </>
        ) : null}
        {(onboarding.allowFATCA && tabKey === TAB_KEY.NEW_REPRESENTANTS) ||
          (tabKey === TAB_KEY.REPRESENTANTS && (
            <>
              <Row className="create-account-user__rows create-account-user__col-margin-top">
                <ThemedText
                  variant="body"
                  fontWeight="semiBold"
                  className="create-account-user__text"
                >
                  {translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.FATCA.TITLE'
                  )}
                </ThemedText>
              </Row>
              <Row
                gutter={16}
                className="create-account-user__rows create-account-user__col-margin-top"
              >
                <Col xs={24} sm={12}>
                  <AdvancedCheckbox
                    label={translate(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.FATCA.TAX_DOMICILE'
                    )}
                    value={hasFiscalResidentialOutside}
                    onChange={() =>
                      setHasFiscalResidentialOutside(
                        !hasFiscalResidentialOutside
                      )
                    }
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <div className="create-account-user__full">
                    <AdvancedCheckbox
                      label={translate(
                        'PAGES.AUTH.REGISTER.ONBOARD.PF.OCCUPATIONS.FATCA.US_PERSON'
                      )}
                      value={isUsPerson}
                      onChange={() => setIsUsPerson(!isUsPerson)}
                    />

                    <Popover content={PopOverContentFacta}>
                      <QuestionCircleOutlined />
                    </Popover>
                  </div>
                </Col>
              </Row>
            </>
          ))}
        {!hidePrivacyPolicy &&
          tabKey !== TAB_KEY.MINOR_REPRESENTANTS &&
          tabKey !== TAB_KEY.NEW_REPRESENTANTS && (
            <Alert
              type="warning"
              showIcon
              className="create-account-user__col-margin-top create-account-user__text"
              message={translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.FIRST_STEP.FOOTER'
              )}
            />
          )}
        <div className="advance_final">
          {tabKey === TAB_KEY.FIRST_STEP &&
          onboarding.enableLogoutOnboardingButton ? (
            <AdvancedButton
              type="button"
              text={translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.ANOTHER_ACCOUNT'
              )}
              variant="outlined"
              onClick={handleLogout}
            />
          ) : null}
          {tabKey && tabKey === TAB_KEY.REPRESENTANTS ? (
            <AdvancedButton
              type="button"
              text={translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.BACK_LABEL'
              )}
              variant="text"
              onClick={() => {
                if (onboarding.recommendation) {
                  setTabKey(TAB_KEY.RECOMMENDATION);
                } else if (navigate?.location?.state?.isRetry) {
                  setTabKey(TAB_KEY.FIRST_STEP);
                } else {
                  setTabKey(TAB_KEY.APPROVAL_DEVICE);
                }
              }}
            />
          ) : null}
          <AdvancedButton
            text={translate(
              'PAGES.AUTH.REGISTER.ONBOARD.PF.BUTTONS.SUBMIT_LABEL'
            )}
            type="button"
            onClick={() => {
              if (handleRepresentsValidation) {
                handleRepresentsValidation(
                  {
                    name,
                    taxIdentifier,
                    email,
                    bornAt,
                    motherName,
                    cellphone,
                    type,
                    companyPercentage,
                    address,
                    identityCardIssueDate,
                    usPerson: isUsPerson,
                    taxResidenceAnotherCountry: hasFiscalResidentialOutside,
                    identityCardNumber: unmaskField(identityCardNumber),
                    main: tabKey === TAB_KEY.REPRESENTANTS,
                  },
                  isCompany,
                  addressFields
                );

                return;
              }

              if (handleFirstStepPerson) {
                if (navigate?.location?.state?.isRetry) {
                  if (!name || !name.split(' ')[1]) {
                    return MessageService.error(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.NAME'
                    );
                  }

                  if (!bornAt) {
                    return MessageService.error(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.BIRTH_DATE'
                    );
                  }

                  if (!motherName || !motherName.split(' ')[1]) {
                    return MessageService.error(
                      'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.NAME'
                    );
                  }
                }

                handleFirstStepPerson({
                  name,
                  taxIdentifier,
                  email,
                  bornAt,
                  identityCardIssueDate: onboarding.identityCardPf
                    ? identityCardIssueDate
                    : undefined,
                  identityCardNumber: onboarding.identityCardPf
                    ? identityCardNumber
                    : undefined,
                  income: onboarding.incomePf ? income : undefined,
                  isPep: onboarding.isPep ? isPep : undefined,
                  motherName,
                  cellphone,
                  gender: !isUserMinor ? gender : undefined,
                  businessLine: !isUserMinor ? businessLine : undefined,
                  financialStatistic: !isUserMinor
                    ? financialStatistic
                    : undefined,
                });

                return;
              }
            }}
          />
        </div>
      </AdvancedForm>
    </div>
  );
};

export default CreateAccountUser;
